import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { Group } from 'react-konva';
import Konva from 'konva';
import { KonvaEventObject } from 'konva/lib/Node';

import { SEMI_TRANPARENT_BLACK_PANEL, FULL_TRANPARENT_BLACK_PANEL, FACET_HIGHLIGHT_COLOR, 
	FACET_HIGHLIGHT_OFF_COLOR, FACET_DEFAULT_FILL_COLOR_ROOF_TOOL, PANEL_ON_COLOR, PANEL_STROKE_COLOR,
	INVALID_PANEL_ARRAY_DRAG_COLOR } from '../../../constants';
import { getExistingFacetsGroupID, getRoofSegmentIDFromGroupID, KonvaGroupNames, resetAllTransformations } from '../utils';
import { getFacetInvalidPaddingPercent, isFacetArrayDragInvalid, normalizePointsToStage } from '../PanelUtil';

import { setDeletedRoofIndexes, setRoofIndexes, setSelectedRoofAzimuth, setSelectedRoofIndex, updateTransition } from 'store/slices/ToolSlice';
import { useRefs } from 'contexts/RefContext';

import Facet from '../Facet';
import PanelsGroup from './PanelsGroup';
import { setDropdownState } from 'store/slices/EditFacetModalSlice';
import TransformerComponent from './PanelsGroup/Transformer';
import TempEditFacetGroup from './TempEditFacetGroup';
import TempEditEdgeGroup from './TempEditEdgeGroup';
import { useLongPress } from 'hooks/useLongPress';
import { appToast } from 'components/Basic/CustomToast';
import { setDeletedPanels } from 'store/slices/PanelSlice';
import { selectIsEditingModeEnabled } from 'store/selectors/toolSelector';
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';
import { selectIsRecomputing } from 'store/slices/DsmShadingSlice/selectors';

function FacetGroup() {
	const { roofSegs, allRoofSegs, designType } = useSelector((state: RootState) => state.roofData.data);
	const { enableEditFacet, showFacetLabels} = useSelector((state: RootState) => state.roofEditData.data);
	const allowedActions = useSelector((state: RootState) => state.roleState.data.allowedActions);
	const editRoofState = useSelector((state: RootState) => state.toolNewPostions.data);
	const { activeRoofTool } = useSelector((state: RootState) => state.roofToolLines.data);
	const { roofIndexes, editModeEnabled, selectedRoofIndex, deletedRoofIndexes,
		moveStage, rotateStage, threeDModelEnabled, azimuthEditedFacets, deleteObstructionsMode, deleteFacetMode, multiFingersPress,
		drawModeEnabled
	} = editRoofState;
	const { showIrradiance, showPanels } = useSelector((state: RootState) => state.Irradiance.data);
	const { tempPanelsGroup, activePanelMode, deleteArrayMode } = useSelector((state: RootState) => state.panelSlice.data);
	const queriesRunningInBackGround = useQueriesRunningInBackGround();
	const isRecomputing = useSelector(selectIsRecomputing);
	const trRef = useRef<Konva.Transformer>(null);
	const currentSelectedFacetGroup = useRef<Konva.Group>();
	const longTouchTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

	// Whole-array drag snapshot (click-facet-with-no-edit-mode flow only); null = no drag in progress.
	const facetArrayDragRef = useRef<{
		panelGroup: Konva.Group;
		facetLine: Konva.Line;
		startPos: Vector2d;
		panels: { line: Konva.Line; stroke: string | CanvasGradient; fill: string | CanvasGradient }[];
		paddingPercent: number;
		isInvalid: boolean;
	} | null>(null);
	const dispatch = useDispatch<AppDispatch>();
	const { konvaRef, selectFacetRef } = useRefs();

	const isAnyEditingModeEnabled = useSelector(selectIsEditingModeEnabled);

	const listenToEventsOnGroup = editModeEnabled &&
		(deleteFacetMode || deleteArrayMode || (!isRecomputing && !isAnyEditingModeEnabled));

	const listening = !moveStage && !rotateStage && !showIrradiance;
	const canEditFacet = allowedActions.includes('translate-panels-to-new-position');
	const isAnyEdgeActionEnabled = enableEditFacet || activeRoofTool;

	const { handleTouchStart, handleTouchEndOrCancel, handleTouchMove, longPressTriggeredRef } = useLongPress({
		delay: 800, // ms
		enabled: listening && !(enableEditFacet || activePanelMode || activeRoofTool),
		onLongPress: openEditFacetPopup,
	});
	const hideFacetGrpInIrradianceMode = (showIrradiance && !showPanels);
	const selectedRoofSegment = useMemo(() => (allRoofSegs.find(r => r.id === selectedRoofIndex)), [selectedRoofIndex, allRoofSegs]);

	useEffect(() => {
		if (!enableEditFacet && appToast.isActive('invalid-facet-error')) {
			appToast.dismiss({id:'invalid-facet-error', containerId:'tool-toast'});
		}
	}, [enableEditFacet]);

	useEffect(() => {
		//Deslect group(highlight off) whenever selection is reset
		if (!selectedRoofIndex && currentSelectedFacetGroup.current && konvaRef?.current) {
			changePanelAlphanes(currentSelectedFacetGroup.current, 'LEAVE');
			trRef.current?.detach();
			konvaRef.current.container().style.cursor = 'default';
			currentSelectedFacetGroup.current = undefined;
			return;
		}
	}, [dispatch, konvaRef, selectedRoofIndex]);

	function addRoofIndex() {
		const roofIndex = getRoofSegmentIDFromGroupID(trRef.current?.getNode()?.attrs.id);
		if (!roofIndex) return;
		if (!(activePanelMode || enableEditFacet || activeRoofTool))
			dispatch(updateTransition({ currentTransitionState: true }));
		if (!roofIndexes.includes(roofIndex))
			dispatch(setRoofIndexes({ roofIndexes: [...roofIndexes, roofIndex] }));
	}

	const makeFacetSelectable = useCallback((facetgroup: Konva.Group) => {
		const roofIndex = getRoofSegmentIDFromGroupID(facetgroup.id());
		if (!roofIndex || roofIndex === selectedRoofIndex) return;
		dispatch(setSelectedRoofIndex({ selectedRoofIndex: roofIndex }));
	}, [selectedRoofIndex, dispatch]);

	const makeFacetTranslative = useCallback((facetgroup: Konva.Group) => {
		trRef.current?.detach();
		if(!canEditFacet)	return;

		const panelGroup = facetgroup.findOne(`.${KonvaGroupNames.existingPanelsGroup}`) as Konva.Group;
		if (!panelGroup?.hasChildren() && konvaRef?.current) {
			konvaRef.current.container().style.cursor = 'default';
			dispatch(setSelectedRoofAzimuth({ selectedRoofAzimuth: null }));
			return;
		}
		
		if (konvaRef?.current)
			konvaRef.current.container().style.cursor = 'move';

		//rotate grp and counter rotate child to point transformer handle towards segments'azimuth
		if (!panelGroup.attrs.rotation && !azimuthEditedFacets[getRoofSegmentIDFromGroupID(facetgroup.id())]) {
			const roofSegmentID = getRoofSegmentIDFromGroupID(facetgroup.id());
			const rs = allRoofSegs.find(r => r.id === roofSegmentID);
			if (!rs) return;
			panelGroup?.rotation(rs.azimuthDegrees);
			panelGroup?.getChildren().forEach(e => e.rotation(-rs.azimuthDegrees));
		}

		panelGroup.draggable(true);
		trRef.current?.setNodes([panelGroup]);
		dispatch(setSelectedRoofAzimuth({ selectedRoofAzimuth: (panelGroup.rotation() + 360) % 360 }));
	}, [allRoofSegs, azimuthEditedFacets, canEditFacet, dispatch, konvaRef]);

	const selectFacet = useCallback((facetId: string) => {
		const facetShape = konvaRef?.current?.findOne('#' + facetId) as Konva.Shape;
		if (!facetShape) return;
		const targetFacetGroup = facetShape.getParent() as Konva.Group;
		if (!targetFacetGroup) return;

		targetFacetGroup.moveToTop();
		targetFacetGroup.getLayer()?.batchDraw();

		const previousSelectedGroup = currentSelectedFacetGroup.current;
		if (previousSelectedGroup && previousSelectedGroup.id() !== targetFacetGroup.id()) {
			changePanelAlphanes(previousSelectedGroup, 'LEAVE');
		}

		changePanelAlphanes(targetFacetGroup, 'ENTER');
		makeFacetTranslative(targetFacetGroup);

		const roofIndex = getRoofSegmentIDFromGroupID(targetFacetGroup.id());
		if (roofIndex) dispatch(setSelectedRoofIndex({ selectedRoofIndex: roofIndex }));

		currentSelectedFacetGroup.current = targetFacetGroup;
	}, [konvaRef, makeFacetTranslative, dispatch]);

	useEffect(() => {
		selectFacetRef.current = selectFacet;
	}, [selectFacet, selectFacetRef]);

	function changePanelAlphanes(facetgroup: Konva.Group, action: 'ENTER' | 'LEAVE') {
		const panelGroup = facetgroup.findOne(`.${KonvaGroupNames.existingPanelsGroup}`) as Konva.Group | undefined;
		if (!panelGroup?.children?.length) return;
		panelGroup.moveToTop();
		trRef.current?.moveToTop();
		const lines = panelGroup?.children as Konva.Line[];
		for (let i = 0; i < lines.length; i++) {
			lines[i].attrs['fill'] = action === 'ENTER' ? FULL_TRANPARENT_BLACK_PANEL : SEMI_TRANPARENT_BLACK_PANEL;
		}
	}

	const roofSegments = useMemo(() => (editModeEnabled || designType === 'ROOF') ? allRoofSegs : Object.values(roofSegs),
		[editModeEnabled, designType, allRoofSegs, roofSegs]);

	useEffect(() => {
		// reset transformations when roofSegments are updated in edit mode
		if (konvaRef?.current && editModeEnabled) {
			dispatch(setSelectedRoofIndex({ selectedRoofIndex: null }));
			resetAllTransformations(konvaRef?.current, designType === 'ROOF');
			konvaRef.current.container().style.cursor = 'default';
			currentSelectedFacetGroup.current = undefined;
		}
	}, [allRoofSegs, konvaRef, editModeEnabled, isAnyEditingModeEnabled , dispatch, designType]);

	const handleGroupClick = useCallback((event: KonvaEventObject<PointerEvent>) => {
		if (event.evt.button === 2) return;

		if (tempPanelsGroup.length > 0) return;
		if (!listenToEventsOnGroup) return;
		const targetFacetGroup = event.currentTarget as Konva.Group;
		const roofSegmentId = getRoofSegmentIDFromGroupID(targetFacetGroup.id());

		if (deleteArrayMode) {
			const panelGroup = targetFacetGroup.findOne(`.${KonvaGroupNames.existingPanelsGroup}`) as Konva.Group;
			if (panelGroup) {
				const allPanels = (panelGroup.children || []) as Konva.Line[];
				if(allPanels.length){
					const deletedPanelIds = allPanels.map(p => ({
						panelId: p.id(),
						roofSegmentId,
					}));
					dispatch(setDeletedPanels({ data: deletedPanelIds }));
				}
				
			}
			return;
		}

		if(deleteFacetMode){
			dispatch(setDeletedRoofIndexes({deletedRoofIndexes : [...deletedRoofIndexes, roofSegmentId]}));
			return;
		}

		targetFacetGroup.moveToTop();
		targetFacetGroup?.getLayer()?.batchDraw();

		const previousSelectedGroup = selectedRoofIndex ? currentSelectedFacetGroup.current : null;
		console.log('facet', targetFacetGroup.id());

		if (longPressTriggeredRef.current && previousSelectedGroup?.id() === targetFacetGroup.id()) {
			longPressTriggeredRef.current = false;
			return;
		}
		// No selected facet -> select
		if (!previousSelectedGroup) {
			changePanelAlphanes(targetFacetGroup, 'ENTER');
			makeFacetTranslative(targetFacetGroup);
			makeFacetSelectable(targetFacetGroup);
			currentSelectedFacetGroup.current = targetFacetGroup;
			return;
		}

		// Switching selection
		if (previousSelectedGroup.id() !== targetFacetGroup.id()) {
			changePanelAlphanes(previousSelectedGroup, 'LEAVE');

			changePanelAlphanes(targetFacetGroup, 'ENTER');
			makeFacetTranslative(targetFacetGroup);
			makeFacetSelectable(targetFacetGroup);
			currentSelectedFacetGroup.current = targetFacetGroup;
			return;
		}

		// Same facet -> UNSELECT
		// if (previousSelectedGroup.id() === targetFacetGroup.id()) {
		// 	changePanelAlphanes(previousSelectedGroup, 'LEAVE');
		// 	dispatch(setSelectedRoofIndex({ selectedRoofIndex: null }));
		// 	trRef.current?.detach();
		// 	currentSelectedFacetGroup.current = undefined;
		// 	return;
		// }
	}, [deleteArrayMode, deleteFacetMode, deletedRoofIndexes, 
		dispatch, listenToEventsOnGroup, makeFacetSelectable, 
		makeFacetTranslative, selectedRoofIndex, 
		tempPanelsGroup.length, longPressTriggeredRef
	]);

	function handleContextMenuEvt(event: KonvaEventObject<PointerEvent>) {
		if (!listenToEventsOnGroup || deleteFacetMode) return;
		event.evt.preventDefault();
		const target = event.currentTarget as Konva.Group;
		const roofSegmentId = getRoofSegmentIDFromGroupID(target.id());

		// If facet is not selected -> select it (but never unselect)
		if (currentSelectedFacetGroup.current?.id() !== target.id()) {
			if (currentSelectedFacetGroup.current) {
				changePanelAlphanes(currentSelectedFacetGroup.current, 'LEAVE');
			}

			changePanelAlphanes(target, 'ENTER');
			makeFacetTranslative(target);
			dispatch(setSelectedRoofIndex({ selectedRoofIndex: roofSegmentId }));
			currentSelectedFacetGroup.current = target;
		}
		openEditFacetPopup({ x: event.evt.clientX, y: event.evt.clientY });
	}

	function openEditFacetPopup(position: Vector2d) {
		dispatch(setDropdownState({ open: true, position }));
	}

	function handleDragStart(e: any) {
		if(multiFingersPress) {
			e.target.stopDrag();
		}
		handleTouchEndOrCancel();
		if (!listenToEventsOnGroup) return;
		if (konvaRef?.current) {
			clearTimeout(longTouchTimerRef.current);
			konvaRef.current.container().style.cursor = 'move';
		}
		// Skip if stopDrag() above already ended the gesture, or this is another panel's dragstart.
		if (!multiFingersPress && e.target.hasName(KonvaGroupNames.existingPanelsGroup)) captureFacetArrayDragStart();
	}

	// Snapshot the panel group, facet boundary, and each panel's current style.
	function captureFacetArrayDragStart() {
		const facetGroup = currentSelectedFacetGroup.current;
		facetArrayDragRef.current = null;
		if (!facetGroup) return;

		const panelGroup = facetGroup.findOne(`.${KonvaGroupNames.existingPanelsGroup}`) as Konva.Group;
		const roofSegmentId = getRoofSegmentIDFromGroupID(facetGroup.id());
		const facetLine = facetGroup.findOne(`#${roofSegmentId}`) as Konva.Line;
		const panelLines = (panelGroup?.children || []) as Konva.Line[];
		if (!panelGroup || !facetLine || !panelLines.length) return;

		const roofSegment = allRoofSegs.find(rs => rs.id === roofSegmentId);
		facetArrayDragRef.current = {
			panelGroup,
			facetLine,
			startPos: panelGroup.position(),
			panels: panelLines.map(line => ({ line, stroke: line.stroke(), fill: line.fill() })),
			paddingPercent: getFacetInvalidPaddingPercent(roofSegment),
			isInvalid: false,
		};
	}

	// Repaint red/original only on a valid<->invalid transition; position is untouched here.
	function handleFacetArrayDragMove(e: KonvaEventObject<DragEvent>) {
		if (!listenToEventsOnGroup) return;
		const drag = facetArrayDragRef.current;
		// Ignore a bubbled dragmove from any other draggable descendant (e.g. an individual panel).
		if (!drag || e.target !== drag.panelGroup) return;

		const facetPolygonPoints = normalizePointsToStage(drag.facetLine);
		const isInvalid = isFacetArrayDragInvalid(drag.panels.map(p => normalizePointsToStage(p.line)), facetPolygonPoints, drag.paddingPercent);
		if (isInvalid === drag.isInvalid) return;

		drag.isInvalid = isInvalid;
		drag.panels.forEach(({ line, stroke, fill }) => {
			line.stroke(isInvalid ? INVALID_PANEL_ARRAY_DRAG_COLOR.stroke : stroke);
			line.fill(isInvalid ? INVALID_PANEL_ARRAY_DRAG_COLOR.fill : fill);
		});
		konvaRef?.current?.batchDraw();
	}

	// Snap the array + every panel's style back to pre-drag; caller skips addRoofIndex to not commit it.
	function revertInvalidFacetArrayDrag() {
		const drag = facetArrayDragRef.current;
		if (!drag) return;

		drag.panelGroup.position(drag.startPos);
		drag.panels.forEach(({ line, stroke, fill }) => {
			line.stroke(stroke);
			line.fill(fill);
		});
		konvaRef?.current?.batchDraw();
	}

	function handleFacetArrayDragEnd(e: KonvaEventObject<DragEvent>) {
		const drag = facetArrayDragRef.current;
		const isThisDrag = !!drag && e.target === drag.panelGroup; // ignore other panels' drag events
		const isInvalid = isThisDrag && drag.isInvalid;

		if (isInvalid) revertInvalidFacetArrayDrag(); // before the gate below: Konva can end this drag itself
		if (isThisDrag) facetArrayDragRef.current = null;

		if (!listenToEventsOnGroup) return;
		if (!isInvalid) addRoofIndex();
		if (konvaRef?.current) konvaRef.current.container().style.cursor = 'default';
	}

	function handleHover(e: Konva.KonvaEventObject<MouseEvent | TouchEvent>, roofSegID: string, type: 'ENTER' | 'LEAVE') {

		if (!listenToEventsOnGroup) return;
		const targetFacetGroup = e.currentTarget as Konva.Group;

		if(!deleteFacetMode && !deleteArrayMode){
			return;
		}

		const container = targetFacetGroup?.getStage()?.container();
		if (container) container.style.cursor = type === 'ENTER' ? 'pointer' : 'default';
		
		if(deleteArrayMode){
			const panelGroup = targetFacetGroup.findOne(`.${KonvaGroupNames.existingPanelsGroup}`) as Konva.Group;

			(panelGroup.find('Line') ?? [] as Konva.Line[]).forEach((node) => {
				const line = (node as Konva.Line);
				line.fill(type === 'ENTER'? 'rgba(0, 128, 255, 0.3)': PANEL_ON_COLOR );
				line.stroke(type === 'ENTER' ? 'rgba(0, 128, 255, 0.5)': PANEL_STROKE_COLOR);
			});

			panelGroup?.getLayer()?.batchDraw();
			return;
		}
		const facet = targetFacetGroup?.findOne(`#${roofSegID}`) as Konva.Line;
		if(!facet) return;
	
		facet.fill(type === 'ENTER' ? FACET_HIGHLIGHT_COLOR : designType === 'SOLAR' ? 
			FACET_HIGHLIGHT_OFF_COLOR : 
			FACET_DEFAULT_FILL_COLOR_ROOF_TOOL
		);
		targetFacetGroup?.getLayer()?.batchDraw();
	}

	return (
		<>
			<Group
				name={`${KonvaGroupNames.existingFacetsGroup}-facets`}
				listening={((!enableEditFacet && !drawModeEnabled) || deleteFacetMode) && !deleteObstructionsMode}
			>
				{roofSegments.map((rs, index) => {
					if (deletedRoofIndexes.includes(rs.id)) return null;
					const groupId = `${getExistingFacetsGroupID(rs.id)}`;
					return (
						<Group
							key={rs.id}
							id={groupId}
							listening={listening}
							// draggable={canEditFacet}
							name={KonvaGroupNames.existingFacetsGroup}
							visible={!hideFacetGrpInIrradianceMode}
							onContextMenu={handleContextMenuEvt}
							onClick={handleGroupClick}
							onTap={handleGroupClick}
							onTouchStart={(e) => {
								if (!listenToEventsOnGroup) return;
								if(!canEditFacet)	return;
								handleTouchStart(e);
							}}
							onTouchEnd={handleTouchEndOrCancel}
							onPointerLeave={() => {
								if (!listenToEventsOnGroup) return;
								if (facetArrayDragRef.current) return; // stay 'move' while actively dragging the array
								if (konvaRef?.current)
									konvaRef.current.container().style.cursor = 'default';
							}}
							onDragStart={handleDragStart}
							onDragMove={handleFacetArrayDragMove}
							onDragEnd={handleFacetArrayDragEnd}
							onTouchMove={handleTouchMove}
							onTouchCancel={handleTouchEndOrCancel}
							onMouseEnter={(e) => handleHover(e, rs.id, 'ENTER')}
							onMouseLeave={(e) => handleHover(e, rs.id, 'LEAVE')}
						>
				
							{!isAnyEdgeActionEnabled && <Facet
								roofSegment={rs}
								selectedRoofID={selectedRoofIndex}
								roofEditingEnabled={designType === 'ROOF'}
								isIrradianceOn={showIrradiance}
								showFacetLabels={showFacetLabels}
							/>}
							{(designType === 'SOLAR') &&
								<PanelsGroup roofSegment={rs} />
							}
						</Group>
					);
				})}
			</Group>

			{/* --------- Edit Facets -----------*/}
			{enableEditFacet && <TempEditFacetGroup 
				roofSegments={roofSegments} 
				deletedRoofSegIDs={deletedRoofIndexes}/>
			}
			{/* --------Edit Edges Group ------- */}
			{activeRoofTool && <TempEditEdgeGroup 
				roofSegments={roofSegments} 
				deletedRoofSegIDs={deletedRoofIndexes}/>
			}

			{/* ------- Transformer ------------*/}
			<TransformerComponent
				ref={trRef}
				visible={!showIrradiance && !threeDModelEnabled}
				selectedRoofSegment={selectedRoofSegment}
				onTransformEnd={addRoofIndex} />
		</>
	);
}
export default memo(FacetGroup);